import Text from "@/components/Text";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import useDetectSizeDevice from "@/hooks/useDetectSizeDevice";
import { createCheckoutSession } from "@/store/checkoutSessionState";
import { currentSubscriptionState } from "@/store/currentSubscriptionState";
import { entitlementsState } from "@/store/entitlementsState";
import { listProductsState } from "@/store/listProductsState";
import { setPaymentAction } from "@/store/paymentActionState";
import {
  Check,
  Headphones,
  Lightbulb,
  Phone,
  Settings,
  Target,
  TrendingUp,
  UsersRound,
  Video,
  X,
} from "lucide-react";
import { type FC } from "react";
import { useTranslation } from "react-i18next";

const ImagesNumber = [1, 2, 3, 4, 5, 6];

const Upgrade: FC = () => {
  const { t } = useTranslation();
  const { sm } = useDetectSizeDevice();

  const { data: listProducts } = listProductsState();
  const { data: stripeSubscription } = currentSubscriptionState();
  const { data: entitlements } = entitlementsState();

  const mainProduct =
    listProducts
      ?.filter((it) => it)
      ?.find((product) => product.isMainSubscription) ?? undefined;

  const isFreeTrial = Boolean(
    entitlements?.find(({ name }) => name === "free_trial"),
  );

  return (
    <div
      style={{
        height: sm
          ? "calc(100dvh - var(--height-header))"
          : "calc(100dvh - var(--height-header) - var(--height-header-not-sm))",
      }}
      className={
        "flex gap-[40px] flex-col bg-[#F7F8F9] w-[100%] p-[16px] sm:px-[80px] sm:pt-[40px] overflow-y-auto overflow-x-hidden"
      }
    >
      <Text fontWeight={600} fontSize={32} color="#172B4D">
        {t("choose_plan_for_you")}
      </Text>

      <Text fontSize={32} fontWeight={600}>
        {t("plans")}
      </Text>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-[16px]">
        <div className="flex flex-col justify-between bg-[white] py-[24px] px-[16px] rounded-[12px]">
          <div className="flex flex-col gap-[32px]">
            <div className="py-[24px] px-[16px] bg-[#000024] gap-[8px] flex flex-col rounded-[8px]">
              <Text fontWeight={500} fontSize={32} color="white">
                {t("starter")}
              </Text>
              <Text fontWeight={400} fontSize={14} color="white">
                {t("observe")}
              </Text>
            </div>
            <div className="flex flex-col gap-[16px]">
              <Text fontSize={28} fontWeight={400}>
                {t("free_price")}
              </Text>
              <div className="flex flex-col gap-[12px]">
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("free_card_1")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("free_card_2")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("free_card_3")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("free_card_4")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("free_card_5")}
                  </Text>
                </div>
              </div>
            </div>
          </div>
          {!stripeSubscription && (
            <Button className="mt-[36px] h-[40px]" variant="secondaryFilled">
              {t("current_plan")}
            </Button>
          )}
        </div>

        <div className="flex flex-col justify-between bg-[white] py-[24px] px-[16px] rounded-[12px]">
          <div className="flex flex-col gap-[32px]">
            <div className="py-[24px] px-[16px] bg-[#000024] gap-[8px] flex flex-col rounded-[8px]">
              <Text fontWeight={500} fontSize={32} color="white">
                {t("pro")}
              </Text>
              <Text fontWeight={400} fontSize={14} color="white">
                {t("understand_market")}
              </Text>
            </div>
            <div className="flex flex-col gap-[16px]">
              <Text fontSize={28} fontWeight={400}>
                {t("pro_price")}
              </Text>
              <div className="flex flex-col gap-[12px]">
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_1")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_2")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_3")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_4")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_5")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_6")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_7")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_8")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_9")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_10")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_11")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_12")}
                  </Text>
                </div>
              </div>
            </div>
          </div>
          {stripeSubscription ? (
            <Button className="mt-[36px] h-[40px]" variant="secondaryFilled">
              {t("current_plan")}
            </Button>
          ) : (
            <Button
              className="mt-[36px] h-[40px]"
              onClick={() => {
                if (mainProduct && mainProduct.defaultPrice) {
                  createCheckoutSession({
                    priceId: mainProduct.defaultPrice,
                  });
                  setPaymentAction("pro");
                }
              }}
            >
              {isFreeTrial ? t("start_free_trial") : t("start_pro")}
            </Button>
          )}
        </div>

        <div className="flex flex-col justify-between bg-[white] py-[24px] px-[16px] rounded-[12px]">
          <div className="flex flex-col gap-[32px]">
            <div className="py-[24px] px-[16px] bg-[#000024] gap-[8px] flex flex-col rounded-[8px]">
              <Text fontWeight={500} fontSize={32} color="white">
                {t("network")}
              </Text>
              <Text fontWeight={400} fontSize={14} color="white">
                {t("scale_piloting")}
              </Text>
            </div>
            <div className="flex flex-col gap-[16px]">
              <Text fontSize={28} fontWeight={400}>
                {t("network_pricing")}
              </Text>

              <div className="flex flex-col gap-[12px]">
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("network_card_1")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("network_card_2")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("network_card_3")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("network_card_4")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("network_card_5")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("network_card_6")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("network_card_7")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("network_card_8")}
                  </Text>
                </div>
              </div>
            </div>
          </div>
          <a
            className="w-[100%] mt-[36px]"
            target="_blank"
            rel="noopener noreferrer"
            href="https://calendly.com/demo-krunsh/30min?month=2026-03"
          >
            <Button className="w-[100%] h-[40px]" variant="primaryOutlined">
              <Phone />
              {t("contact_expert")}
            </Button>
          </a>
        </div>
      </div>

      <div>
        <div className="gap-[38px] flex justify-evenly mt-[40px] mb-[40px] overflow-x-auto">
          {ImagesNumber.map((image) => (
            <div
              key={`image-brand-${image}`}
              className="rounded-[12px] flex items-center justify-center h-[80px] w-[112px] min-w-[112px] p-[20px]"
              style={{
                backgroundColor:
                  image === 1 ? "#221F20" : image === 6 ? "#FF5102" : "white",
              }}
            >
              <img
                className="objec-cover max-h-[80px]"
                src={`/img/pricing/image_${image}.png`}
              />
            </div>
          ))}
        </div>
      </div>

      <Text fontSize={32} fontWeight={600}>
        {t("compare_features")}
      </Text>
      <div
        style={{
          width: "100%",
        }}
      >
        <table
          style={{
            tableLayout: "fixed",
            display: sm ? "table" : "block",
            backgroundColor: "white",
            whiteSpace: "nowrap",
            overflow: "auto",
            width: "100%",
          }}
        >
          {/* header table */}
          <thead
            style={{
              width: "100%",
            }}
          >
            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <th
                style={{
                  border: "1px solid #E5E7EB",
                  minWidth: 200,
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
                className="text-start w-1/4"
              >
                <Text fontSize={14} fontWeight={400} color="#4A5565">
                  {t("functionality")}
                </Text>
              </th>
              <th
                style={{
                  border: "1px solid #E5E7EB",
                  minWidth: 200,
                }}
                className="w-1/4"
              >
                <Text fontSize={14} fontWeight={400} color="#0A0A0A">
                  {t("free")}
                </Text>
              </th>
              <th
                style={{
                  border: "1px solid #E5E7EB",
                  minWidth: 200,
                  backgroundColor: "#EFF6FF",
                }}
                className="w-1/4"
              >
                <Text fontSize={14} fontWeight={400} color="#0A0A0A">
                  {t("radar")}
                </Text>
              </th>
              <th
                style={{
                  border: "1px solid #E5E7EB",
                  minWidth: 200,
                }}
                className="w-1/4"
              >
                <Text fontSize={14} fontWeight={400} color="#0A0A0A">
                  {t("network")}
                </Text>
              </th>
            </tr>
          </thead>

          {/* content table */}
          <tbody>
            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_1")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_2")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_3")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_4")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_5")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_6")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_7")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_8")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_9")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_10")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_11")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_12")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_13")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_14")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_15")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_23")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_24")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_16")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_17")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_18")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_19")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_20")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_21")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <X color="#9FADBC" height={20} width={20} />
                </div>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex items-center justify-center">
                  <Check color="#0F62FE" height={20} width={20} />
                </div>
              </td>
            </tr>

            <tr
              style={{
                border: "1px solid #E5E7EB",
                width: "100%",
                height: 56,
              }}
            >
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 16,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_22")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  textAlign: "center",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_free")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  textAlign: "center",
                  backgroundColor: "#EFF6FF",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_core")}
                </Text>
              </td>
              <td
                style={{
                  border: "1px solid #E5E7EB",
                  textAlign: "center",
                }}
              >
                <Text fontSize={14} fontWeight={400} color="#101828">
                  {t("pricing_table_network")}
                </Text>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-[26px] bg-[white] p-[16px] sm:p-[40px] rounded-[12px]">
        <div className="flex flex-col">
          <Text
            fontWeight={600}
            fontSize={32}
            color="#172B4D"
            style={{ lineHeight: "40px" }}
          >
            {t("pricing_card_1_title")}
          </Text>
          <Text
            fontWeight={600}
            fontSize={32}
            color="#172B4D"
            style={{ lineHeight: "40px" }}
          >
            {t("pricing_card_1_subtitle")}
          </Text>
          <Text
            fontWeight={400}
            fontSize={16}
            style={{ lineHeight: "24px", marginTop: 16 }}
          >
            {t("pricing_card_1_description")}
          </Text>
          <Text fontWeight={400} fontSize={16} style={{ lineHeight: "24px" }}>
            {t("pricing_card_1_subdescription")}
          </Text>
          <Text
            fontWeight={400}
            fontSize={16}
            style={{ lineHeight: "24px", marginTop: 16 }}
          >
            {t("pricing_card_1_details")}
          </Text>
          <div className="flex items-center gap-[12px] mt-[16px]">
            <div className="flex items-center justify-center h-[40px] w-[40px] rounded-[10px] bg-[#EFF6FF]">
              <Target height={20} width={20} color="#155DFC" />
            </div>
            <Text fontSize={14} fontWeight={500} color="#172B4D">
              {t("pricing_card_details_1")}
            </Text>
          </div>
          <div className="flex items-center gap-[12px] mt-[12px]">
            <div className="flex items-center justify-center h-[40px] w-[40px] rounded-[10px] bg-[#EFF6FF]">
              <UsersRound height={20} width={20} color="#155DFC" />
            </div>
            <Text fontSize={14} fontWeight={500} color="#172B4D">
              {t("pricing_card_details_2")}
            </Text>
          </div>
          <div className="flex items-center gap-[12px] mt-[12px]">
            <div className="flex items-center justify-center h-[40px] w-[40px] rounded-[10px] bg-[#EFF6FF]">
              <TrendingUp height={20} width={20} color="#155DFC" />
            </div>
            <Text fontSize={14} fontWeight={500} color="#172B4D">
              {t("pricing_card_details_3")}
            </Text>
          </div>

          <Button
            className="mt-[20px]"
            disabled={Boolean(stripeSubscription)}
            onClick={() => {
              if (mainProduct && mainProduct.defaultPrice) {
                createCheckoutSession({
                  priceId: mainProduct.defaultPrice,
                });
                setPaymentAction("pro");
              }
            }}
          >
            {isFreeTrial ? t("start_free_trial") : t("start_pro")}
          </Button>
        </div>
        <div className="flex items-center justify-center">
          <img src="/img/pricing/visibility_restaurant.png" />
        </div>
      </div>

      <div className="w-[100%] bg-[#FFFFFF] rounded-[24px] px-[24px] py-[24px] sm:p-[48px] flex flex-col">
        <Text fontSize={30} fontWeight={500}>
          {t("pricing_card_contact_title")}
        </Text>
        <Text fontSize={16} fontWeight={400} className="mt-[16px] mb-[32px]">
          {t("pricing_card_contact_subtitle")}
        </Text>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://calendly.com/demo-krunsh/30min?month=2026-03"
        >
          <Button className="h-[48px] mb-[48px] w-[min-content]">
            <Phone color="white" />
            {t("pricing_card_contact_action")}
          </Button>
        </a>

        <div className="grid grid-cols-1 sm:grid-cols-2 w-[100%] gap-[12px]">
          <div className="flex items-center gap-[12px]">
            <div className="bg-[#EFF6FF] h-[40px] w-[40px] rounded-[10px] flex items-center justify-center">
              <Video height={20} width={20} color="#155DFC" />
            </div>
            <Text fontSize={14} fontWeight={500} className="text-center">
              {t("pricing_card_contact_description_1")}
            </Text>
          </div>
          <div className="flex items-center gap-[12px]">
            <div className="bg-[#EFF6FF] h-[40px] w-[40px] rounded-[10px] flex items-center justify-center">
              <Lightbulb height={20} width={20} color="#155DFC" />
            </div>
            <Text fontSize={14} fontWeight={500} className="text-center">
              {t("pricing_card_contact_description_2")}
            </Text>
          </div>
          <div className="flex items-center gap-[12px]">
            <div className="bg-[#EFF6FF] h-[40px] w-[40px] rounded-[10px] flex items-center justify-center">
              <Settings height={20} width={20} color="#155DFC" />
            </div>
            <Text fontSize={14} fontWeight={500} className="text-center">
              {t("pricing_card_contact_description_3")}
            </Text>
          </div>
          <div className="flex items-center gap-[12px]">
            <div className="bg-[#EFF6FF] h-[40px] w-[40px] rounded-[10px] flex items-center justify-center">
              <Headphones height={20} width={20} color="#155DFC" />
            </div>
            <Text fontSize={14} fontWeight={500} className="text-center">
              {t("pricing_card_contact_description_4")}
            </Text>
          </div>
        </div>
      </div>

      <div className="bg-[#000024] w-[100%] p-[40px] rounded-[12px] flex flex-col items-center">
        <Text
          fontSize={16}
          fontWeight={400}
          color="white"
          className="text-center"
          style={{ fontStyle: "italic" }}
        >
          {t("pricing_comment_line_1")}
        </Text>
        <Text
          fontSize={16}
          fontWeight={400}
          color="white"
          className="text-center"
          style={{ fontStyle: "italic" }}
        >
          {t("pricing_comment_line_2")}
        </Text>
        <div className="flex items-center mt-[42px]">
          <img src="/img/pricing/matthieu.png" height={64} width={64} />
          <div className="flex flex-col gap-[4px] ml-[16px]">
            <Text
              fontSize={16}
              fontWeight={500}
              style={{ lineHeight: "24px" }}
              color="white"
            >
              Matthieu T.
            </Text>
            <Text
              fontSize={16}
              fontWeight={300}
              style={{ lineHeight: "24px" }}
              color="white"
            >
              {t("multi_franchisee")}
            </Text>
          </div>
        </div>
      </div>

      <Text fontSize={32} fontWeight={600} color="#172B4D">
        {t("faq_krunsh")}
      </Text>

      <Accordion type="single" collapsible>
        <AccordionItem value="first">
          <AccordionTrigger className="items-center">
            <Text fontSize={18} fontWeight={500} color="#172B4D">
              {t("faq_krunsh_title_1")}
            </Text>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col">
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_1_1")}
            </Text>
            <Text
              color="#4A5565"
              fontSize={16}
              fontWeight={400}
              className="mt-[12px]"
            >
              {t("faq_krunsh_description_1_2")}
            </Text>
            <Text
              color="#4A5565"
              fontSize={16}
              fontWeight={400}
              className="mt-[12px]"
            >
              {t("faq_krunsh_description_1_3")}
            </Text>
            <div className="pl-[12px] mt-[8px] mb-[24px]">
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_1_4")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_1_5")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_1_6")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_1_7")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_1_8")}
                </Text>
              </div>
            </div>
            <Text
              color="#4A5565"
              fontSize={16}
              fontWeight={400}
              className="mt-[12px]"
            >
              {t("faq_krunsh_description_1_9")}
            </Text>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="second">
          <AccordionTrigger className="items-center">
            <Text fontSize={18} fontWeight={500} color="#172B4D">
              {t("faq_krunsh_title_2")}
            </Text>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col">
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_2_1")}
            </Text>
            <Text
              color="#4A5565"
              fontSize={16}
              fontWeight={400}
              className="mt-[12px]"
            >
              {t("faq_krunsh_description_2_2")}
            </Text>
            <div className="pl-[12px] mt-[8px] mb-[24px]">
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_2_3")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_2_4")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_2_5")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_2_6")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_2_7")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_2_8")}
                </Text>
              </div>
            </div>
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_2_9")}
            </Text>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="third">
          <AccordionTrigger className="items-center">
            <Text fontSize={18} fontWeight={500} color="#172B4D">
              {t("faq_krunsh_title_3")}
            </Text>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col">
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_3_1")}
            </Text>
            <Text
              color="#4A5565"
              fontSize={16}
              fontWeight={400}
              className="mt-[12px]"
            >
              {t("faq_krunsh_description_3_2")}
            </Text>
            <div className="pl-[12px] mt-[8px] mb-[24px]">
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_3_3")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_3_4")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_3_5")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_3_6")}
                </Text>
              </div>
            </div>
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_3_7")}
            </Text>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="five">
          <AccordionTrigger className="items-center">
            <Text fontSize={18} fontWeight={500} color="#172B4D">
              {t("faq_krunsh_title_5")}
            </Text>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col">
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_5_1")}
            </Text>

            <div className="pl-[12px] mt-[8px] mb-[24px]">
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_5_2")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_5_3")}
                </Text>
              </div>
            </div>
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_5_4")}
            </Text>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="six">
          <AccordionTrigger className="items-center">
            <Text fontSize={18} fontWeight={500} color="#172B4D">
              {t("faq_krunsh_title_6")}
            </Text>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col">
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_6_1")}
            </Text>
            <Text
              color="#4A5565"
              fontSize={16}
              fontWeight={400}
              className="mt-[12px]"
            >
              {t("faq_krunsh_description_6_2")}
            </Text>
            <div className="pl-[12px] mt-[8px] mb-[24px]">
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_6_3")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_6_4")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_6_5")}
                </Text>
              </div>
            </div>
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_6_6")}
            </Text>
            <div className="pl-[12px] mt-[8px] mb-[24px]">
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_6_7")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_6_8")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_6_9")}
                </Text>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="seven">
          <AccordionTrigger className="items-center">
            <Text fontSize={18} fontWeight={500} color="#172B4D">
              {t("faq_krunsh_title_7")}
            </Text>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col">
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_7_1")}
            </Text>
            <Text
              color="#4A5565"
              fontSize={16}
              fontWeight={600}
              className="mt-[12px]"
            >
              {t("faq_krunsh_description_7_2")}
            </Text>
            <div className="pl-[12px] mt-[8px] mb-[24px]">
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_7_3")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_7_4")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_7_5")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_7_6")}
                </Text>
              </div>
            </div>
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_7_7")}
            </Text>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="nine">
          <AccordionTrigger className="items-center">
            <Text fontSize={18} fontWeight={500} color="#172B4D">
              {t("faq_krunsh_title_9")}
            </Text>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col">
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_9_1")}
            </Text>
            <Text
              color="#4A5565"
              fontSize={16}
              fontWeight={400}
              className="mt-[12px]"
            >
              {t("faq_krunsh_description_9_2")}
            </Text>
            <div className="pl-[12px] mt-[8px] mb-[24px]">
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_9_3")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_9_4")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_9_5")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_9_6")}
                </Text>
              </div>
            </div>
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_9_7")}
            </Text>
            <div className="pl-[12px] mt-[8px] mb-[24px]">
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_9_8")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_9_9")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_9_10")}
                </Text>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="ten">
          <AccordionTrigger className="items-center">
            <Text fontSize={18} fontWeight={500} color="#172B4D">
              {t("faq_krunsh_title_10")}
            </Text>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col">
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_10_1")}
            </Text>
            <Text
              color="#4A5565"
              fontSize={16}
              fontWeight={400}
              className="mt-[12px]"
            >
              {t("faq_krunsh_description_10_2")}
            </Text>
            <div className="pl-[12px] mt-[8px] mb-[24px]">
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_10_3")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_10_4")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_10_5")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_10_6")}
                </Text>
              </div>
            </div>
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_10_7")}
            </Text>
            <div className="pl-[12px] mt-[8px] mb-[24px]">
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_10_8")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_10_9")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_10_10")}
                </Text>
              </div>
            </div>
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_10_11")}
            </Text>
            <Text
              color="#4A5565"
              fontSize={16}
              fontWeight={400}
              className="mt-[12px]"
            >
              {t("faq_krunsh_description_10_12")}
            </Text>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="eleven">
          <AccordionTrigger className="items-center">
            <Text fontSize={18} fontWeight={500} color="#172B4D">
              {t("faq_krunsh_title_11")}
            </Text>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col">
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_11_1")}
            </Text>
            <div className="pl-[12px] mt-[8px] mb-[24px]">
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_11_2")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_11_3")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_11_4")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_11_5")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_11_6")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_11_7")}
                </Text>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="twelve">
          <AccordionTrigger className="items-center">
            <Text fontSize={18} fontWeight={500} color="#172B4D">
              {t("faq_krunsh_title_12")}
            </Text>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col">
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_12_1")}
            </Text>
            <div className="pl-[12px] mt-[8px] mb-[24px]">
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_12_2")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_12_3")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_12_4")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_12_5")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_12_6")}
                </Text>
              </div>
            </div>
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_12_7")}
            </Text>
            <div className="pl-[12px] mt-[8px] mb-[24px]">
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_12_8")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_12_9")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_12_10")}
                </Text>
              </div>
              <div className="flex items-center gap-[12px]">
                <div className="h-[4px] w-[4px] rounded-[50%] bg-[#44546F]" />
                <Text
                  fontSize={14}
                  fontWeight={400}
                  style={{ lineHeight: "24px" }}
                >
                  {t("faq_krunsh_description_12_11")}
                </Text>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="thirteen">
          <AccordionTrigger className="items-center">
            <Text fontSize={18} fontWeight={500} color="#172B4D">
              {t("faq_krunsh_title_13")}
            </Text>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col">
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_13_1")}
            </Text>
            <Text
              color="#4A5565"
              fontSize={16}
              fontWeight={400}
              className="mt-[12px]"
            >
              {t("faq_krunsh_description_13_2")}
            </Text>
            <Text color="#4A5565" fontSize={16} fontWeight={400}>
              {t("faq_krunsh_description_13_3")}
            </Text>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default Upgrade;
